import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import AiAgentService from '../../api/AiAgentService';
import MessageDetailsPanel from '../MessageDetailsPanel';
import NoteCreationModal from '../NoteCreationModal';
import useFormatDate from '../../hooks/useFormatDate';
import './style.css';
import { RiRobot2Line } from "react-icons/ri";
import { TiUser } from "react-icons/ti";
import { AiFillInfoCircle } from "react-icons/ai";
import { MdNoteAdd } from "react-icons/md";
import instance from "../../api.config";

const AiChatContainer = ({ currentChat, onChatUpdate }) => {
    const customer = useSelector(state => state.customer.selectedCustomer);
    const { formatDateTime } = useFormatDate();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [messageDetails, setMessageDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const [selectedNoteMessage, setSelectedNoteMessage] = useState(null);
    const messagesEndRef = React.useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        const fetchMessages = async () => {
            if (!currentChat) return;
            
            setLoading(true);
            try {
                const response = await AiAgentService.getChatMessages(currentChat.id);
               
                setMessages(response.messages || []);
            } catch (error) {
                console.error('Error fetching messages:', error);
                setMessages([]);
            } finally {
                setLoading(false);
            }
        };

        const fetchCompanyName = async () => {
            try {
                const companyResponse = await instance.post('/getCompanyNameById', {
                    main_url: customer?.server_url
                });
                setCompanyName(companyResponse?.data?.title || '');
            } catch (error) {
                console.error('Error fetching company name:', error);
            }
        };

        if (currentChat) {
            fetchMessages();
            if (customer) {
                fetchCompanyName();
            }
        } else {
            setMessages([]);
        }
    }, [currentChat, customer]);

    const handleInfoClick = async (messageId) => {
        if (selectedMessage === messageId) {
            setSelectedMessage(null);
            setMessageDetails(null);
            return;
        }

        setSelectedMessage(messageId);
        setLoadingDetails(true);
        
        try {
            const response = await AiAgentService.getDetailMessage(messageId);
            setMessageDetails(response);
        } catch (error) {
            console.error('Error fetching message details:', error);
            setMessageDetails(null);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleNoteClick = (message, index) => {
        // Знаходимо попереднє повідомлення користувача
        let userMessage = '';
        for (let i = index - 1; i >= 0; i--) {
            if (messages[i].sender_type === 'user') {
                userMessage = messages[i].content;
                break;
            }
        }

        setSelectedNoteMessage({
            id: message.id,
            aiMessage: message.content,
            userMessage: userMessage
        });
        setNoteModalOpen(true);
    };

    const handleCloseNoteModal = () => {
        setNoteModalOpen(false);
        setSelectedNoteMessage(null);
    };

    const renderUsedTools = (tools) => {
        if (!tools || tools.length === 0) return null;
        
        return (
            <div className="used-tools">
                <small className="tools-label">Використані інструменти:</small>
                <div className="tools-list">
                    {tools.map((tool, index) => (
                        <span key={index} className="tool-badge">
                            {tool}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    const renderFiles = (files) => {
        if (!files || files.length === 0) return null;
        
        return (
            <div className="message-files">
                <small className="files-label">Прикріплені файли:</small>
                <div className="files-grid">
                    {files.map((fileName, index) => (
                        <div key={index} className="file-item">
                            <img 
                                src={`${process.env.REACT_APP_UPLOAD_PATH_UI}${fileName}`} 
                                alt={fileName}
                                className="file-image"
                                onClick={() => window.open(`${process.env.REACT_APP_UPLOAD_PATH_UI}${fileName}`, '_blank')}
                            />
                            <small className="file-name">{fileName}</small>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderStopReason = (stopReason) => {
        if (!stopReason || !stopReason?.type) return null;
        
        const isError = stopReason?.type === 'error';
        
        return (
            <div className={`stop-reason ${isError ? 'error' : 'user-stopped'}`}>
                <small className="stop-reason-label">
                    {isError ? '⚠️ Помилка:' : '⏹️ Зупинено:'}
                </small>
                <small className="stop-reason-message">{stopReason?.message}</small>
            </div>
        );
    };

    if (!currentChat) {
        return (
            <div className="ai-chat-container">
                <div className="no-chat-selected">
                    <h3>Оберіть чат зі списку</h3>
                    <p>Виберіть чат зі списку ліворуч, щоб переглянути повідомлення</p>
                </div>
            </div>
        );
    }

    return (
        <div className="ai-chat-container">
            <div className="ai-chat-header">
                <h3 style={{margin: 0}}>{currentChat.customer ? `${currentChat.customer.customer_name} (${currentChat.customer.role_title})` : currentChat.title}</h3>
                <div className="chat-info">
                    <span>Всього повідомлень: {currentChat.messages_count}</span>
                    {customer && (
                        <span>Сервер: {customer.server_url}</span>
                    )}
                    <span>Назва: {companyName || 'Невідомо'}</span>
                    <span>Створено: {formatDateTime(currentChat.created_at)}</span>
                    <span>Оновлено: {formatDateTime(currentChat.updated_at)}</span>
                </div>
            </div>
            
            <div className="ai-messages-container">
                {loading ? (
                    <div className="loading">Завантаження повідомлень...</div>
                ) : messages.length === 0 ? (
                    <div className="no-messages">
                        <p>Повідомлень немає</p>
                    </div>
                ) : (
                    messages.map((message, index) => (
                        <div key={message.id} className={`ai-message ${message.sender_type}`}>
                            <div className="message-header">
                                <span className="sender-type">
                                    {message.sender_type === 'user' ? 
                                        <>
                                            <TiUser size={'20px'} /> 
                                            <span 
                                                className="customer-info"
                                                title={customer ? 
                                                    `Server: ${customer.server_url}\nСтворено: ${formatDateTime(customer.created_at)}\nОновлено: ${formatDateTime(customer.updated_at)}` 
                                                    : ''
                                                }
                                            >
                                                {customer?.customer_name || 'Користувач'}
                                            </span>
                                        </> 
                                        : <>
                                            <RiRobot2Line size={'20px'} /> 
                                            AI Асистент 
                                            <AiFillInfoCircle 
                                                size={'20px'} 
                                                className="info-icon"
                                                onClick={() => handleInfoClick(message.id)}
                                            />
                                            <MdNoteAdd 
                                                size={'20px'} 
                                                className="note-icon"
                                                onClick={() => handleNoteClick(message, index)}
                                                title="Створити нотатку"
                                            />
                                        </>
                                    }
                                </span>
                                <span className="message-time">
                                    {formatDateTime(message.created_at)}
                                </span>
                            </div>
                            <div className="message-content">
                                {message.content}
                            </div>
                            {renderStopReason(message?.stop_reason)}
                            {renderFiles(message.files)}
                            {renderUsedTools(message.used_tools)}
                        </div>
                    ))
                )}
                <div ref={messagesEndRef} />
            </div>
            
            <MessageDetailsPanel
                isOpen={!!selectedMessage}
                onClose={() => {
                    setSelectedMessage(null);
                    setMessageDetails(null);
                }}
                messageDetails={messageDetails}
                loadingDetails={loadingDetails}
                formatDate={formatDateTime}
            />

            <NoteCreationModal
                isOpen={noteModalOpen}
                onClose={handleCloseNoteModal}
                userMessage={selectedNoteMessage?.userMessage || ''}
                aiMessage={selectedNoteMessage?.aiMessage || ''}
                messageId={selectedNoteMessage?.id || null}
            />
        </div>
    );
};

export default AiChatContainer;
